import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, TrendingUp } from 'lucide-react';

// Use window.location.origin so nginx proxy handles backend routing
const BACKEND_URL = window.location.origin;

export const PublicRoadmap = () => {
  const { tenantSlug } = useParams();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [projectName, setProjectName] = useState(null);
  const [loading, setLoading] = useState(true);

  const STATUS_COLUMNS = [
    { id: 'planned', label: t.planned, color: 'bg-blue-100 dark:bg-blue-900/20', textColor: 'text-blue-900 dark:text-blue-300' },
    { id: 'in_development', label: t.inDevelopment, color: 'bg-purple-100 dark:bg-purple-900/20', textColor: 'text-purple-900 dark:text-purple-300' },
    { id: 'testing', label: t.testing, color: 'bg-yellow-100 dark:bg-yellow-900/20', textColor: 'text-yellow-900 dark:text-yellow-300' },
    { id: 'completed', label: t.completed, color: 'bg-green-100 dark:bg-green-900/20', textColor: 'text-green-900 dark:text-green-300' },
  ];

  useEffect(() => {
    if (tenantSlug) {
      fetchRoadmap();
      fetchProjectName();
    }
  }, [tenantSlug]);

  const fetchProjectName = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tenants/lookup/${tenantSlug}`);
      setProjectName(response.data.name);
    } catch (error) {
      console.error('Failed to fetch project name:', error);
    }
  };

  const fetchRoadmap = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/roadmap/${tenantSlug}`);

      // Ensure response.data is an array
      const roadmapItems = Array.isArray(response.data) ? response.data : [];
      setItems(roadmapItems);

      // Increment views for feedbacks that have feedback_id (only once per session)
      const viewedFeedbacks = JSON.parse(localStorage.getItem(`viewed_feedbacks_${tenantSlug}`) || '[]');
      const itemsToIncrement = roadmapItems.filter(
        item => item.feedback_id && !viewedFeedbacks.includes(item.feedback_id)
      );
      
      if (itemsToIncrement.length > 0) {
        // Mark as viewed
        const newViewedFeedbacks = [...viewedFeedbacks, ...itemsToIncrement.map(item => item.feedback_id)];
        localStorage.setItem(`viewed_feedbacks_${tenantSlug}`, JSON.stringify(newViewedFeedbacks));
        
        // Increment views in backend
        itemsToIncrement.forEach(async (item) => {
          try {
            await axios.post(`${BACKEND_URL}/api/public/feedbacks/${item.feedback_id}/increment-view`);
          } catch (error) {
            console.error(`Failed to increment view for feedback ${item.feedback_id}:`, error);
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch roadmap:', error);
      toast.error(t.failedToLoadRoadmap);
    } finally {
      setLoading(false);
    }
  };

  const getItemsByStatus = (status) => {
    return items.filter(item => item.status === status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-900 dark:text-white">{t.loadingRoadmap}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{projectName || tenantSlug || 'Project'} - Roadmap | FeedPanels</title>
        <meta name="description" content={`View the product roadmap for ${projectName || 'this project'}. See what features are planned, in development, and coming soon.`} />
        <link rel="canonical" href={`https://feedpanels.com/roadmap/${tenantSlug}`} />
      </Helmet>
      <PublicHeader
        title={t.productRoadmap}
        description={t.seeWhatWeWorkingOn}
        projectName={projectName}
        tenantSlug={tenantSlug}
      />

      <div className="max-w-7xl mx-auto px-3 md:px-6 py-6 md:py-8">
        {items.length === 0 ? (
          <Card className="p-8 md:p-12 text-center">
            <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">{t.noRoadmapItemsYet}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 overflow-x-auto pb-4">
            {STATUS_COLUMNS.map((column) => (
              <div key={column.id} className="space-y-2 md:space-y-4 flex-shrink-0 w-full sm:w-auto">
                <div className={`${column.color} rounded-lg md:rounded-xl p-3 md:p-4`}>
                  <h3 className={`font-bold text-base md:text-lg ${column.textColor}`} data-testid={`roadmap-column-${column.id}`}>
                    {column.label}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {getItemsByStatus(column.id).length} {t.items}
                  </p>
                </div>
                <div className="space-y-2 md:space-y-3">
                  {getItemsByStatus(column.id).map((item) => (
                    <Card key={item.id} data-testid={`roadmap-item-${item.id}`}>
                      <CardContent className="p-2 md:p-4">
                        <h4 className="font-semibold text-xs md:text-sm text-gray-900 dark:text-white mb-2 line-clamp-2">{item.title}</h4>
                        <p className="text-[11px] md:text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{item.description}</p>
                        <div className="flex items-center justify-between gap-1 text-[10px] md:text-xs">
                          {item.quarter && (
                            <Badge variant="outline" className="flex-shrink-0">
                              <Calendar className="w-2 md:w-3 h-2 md:h-3 mr-0.5 md:mr-1" />
                              {item.quarter}
                            </Badge>
                          )}
                          {item.votes > 0 && (
                            <div className="flex items-center text-gray-500 dark:text-gray-400 flex-shrink-0">
                              <TrendingUp className="w-2 md:w-3 h-2 md:h-3 mr-0.5 md:mr-1" />
                              {item.votes}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
};